import { useEffect, useState } from 'react';

import Switch from '@components/switch/Switch';
import UserPermissionTypes from '@constants/permissions';
import { STORAGE_KEYS } from '@constants/storage';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';

import {
  useGetLeadOrgConfigQuery,
  useUpdateLeadOrgConfigMutation
} from '../../queries/LeadManagementQuery';

const AgentReassignmentToggle = () => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { createErrorAlert } = useNotify();
  const { checkPermission } = useCheckPermission();

  const hasConfigView = checkPermission([
    UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_VIEW
  ]);
  const hasConfigEdit = checkPermission([
    UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_EDIT
  ]);

  const { data: orgConfig } = useGetLeadOrgConfigQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId || !hasConfigView }
  );

  const [updateOrgConfig, { isLoading: isUpdating }] =
    useUpdateLeadOrgConfigMutation();

  const [isEnabled, setIsEnabled] = useState<boolean>(false);

  const handleToggle = async (value: boolean) => {
    setIsEnabled(value);
    try {
      await updateOrgConfig({
        organizationId: organizationId || '',
        body: { is_lead_assignment_allowed_for_agent: value }
      }).unwrap();
    } catch {
      setIsEnabled(orgConfig?.is_lead_assignment_allowed_for_agent ?? false);
      createErrorAlert('Failed to update setting. Please try again.');
    }
  };

  useEffect(() => {
    if (orgConfig) setIsEnabled(orgConfig.is_lead_assignment_allowed_for_agent);
  }, [orgConfig]);

  if (!hasConfigView) return null;

  return (
    <div className="flex flex-col rounded-xl bg-white">
      <div className="border-b border-cofeeGreyBlue200 px-6 py-4 text-lg font-semibold text-cofeeNeutral10">
        Lead Assignment Settings
      </div>
      <div className="flex items-center gap-2 p-6">
        <Switch
          value={isEnabled}
          handleClick={handleToggle}
          disabled={!hasConfigEdit || isUpdating}
          testId="agent-reassignment"
        />
        <div className="text-base font-medium text-cofeeNeutral13">
          Allow Agents to re-assign leads
        </div>
      </div>
    </div>
  );
};

export default AgentReassignmentToggle;
